import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, ArrowDownRight, ArrowUpRight, Building2 } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBranchStore } from '@/stores';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const BucketColors = { current: '#10b981', '1_30': '#3b82f6', '31_60': '#f59e0b', '61_90': '#f97316', over_90: '#ef4444' };
const BucketLabels = { current: 'Current', '1_30': '1-30 days', '31_60': '31-60 days', '61_90': '61-90 days', over_90: '90+ days' };

const AgingTable = ({ rows, nameField, testId }) => (
  <table className="w-full text-sm" data-testid={testId}>
    <thead>
      <tr className="border-b bg-muted/50 dark:bg-foreground text-muted-foreground">
        <th className="text-left p-3 font-medium">{nameField === 'customer' ? 'Customer' : 'Supplier'}</th>
        {Object.entries(BucketLabels).map(([k, v]) => <th key={k} className="text-right p-3 font-medium">{v}</th>)}
        <th className="text-right p-3 font-medium">Total</th>
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No outstanding items</td></tr>}
      {rows.map((r, i) => (
        <tr key={i} className="border-b hover:bg-muted/50">
          <td className="p-3 font-medium">{r[nameField]}</td>
          {Object.keys(BucketLabels).map(k => (
            <td key={k} className="p-3 text-right font-mono" style={{ color: r[k] > 0 ? BucketColors[k] : '#a8a29e' }}>
              {r[k] > 0 ? r[k].toLocaleString() : '—'}
            </td>
          ))}
          <td className="p-3 text-right font-mono font-bold">{r.total.toLocaleString()}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function AgingReportPage() {
  const { t } = useLanguage();
  const [arData, setArData] = useState(null);
  const [apData, setApData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchId, setBranchId] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('branch') || new URLSearchParams(window.location.search).get('branch_id') || localStorage.getItem('report_branch_ctx') || 'all'; } catch { return 'all'; }
  });  // iter945 — honour the hub's Global Branch Context
  const { branches, fetchBranches } = useBranchStore();

  useEffect(() => { fetchBranches?.(); }, [fetchBranches]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const params = (branchId && branchId !== 'all') ? { branch_id: branchId } : {};
        const [ar, ap] = await Promise.all([
          api.get('/accounting/receivable-aging', { params }),
          api.get('/accounting/payable-aging', { params }),
        ]);
        setArData(ar.data);
        setApData(ap.data);
      } catch { toast.error('Failed to load aging data'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [branchId]);

  const summaryChart = (summary) =>
    Object.entries(summary || {}).map(([k, v]) => ({ name: BucketLabels[k], value: v, fill: BucketColors[k] }));

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="aging-report-page">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-200/50">
            <Clock size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">
              {t('aging_title') || 'Aging Reports'}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">Track overdue receivables and payables by age</p>
          </div>
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="w-44 h-9" data-testid="aging-branch-filter">
              <div className="flex items-center gap-1.5"><Building2 size={14} /><SelectValue placeholder="Branch" /></div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-warning border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="ar">
            <TabsList className="mb-4">
              <TabsTrigger value="ar" className="gap-1" data-testid="ar-tab"><ArrowDownRight size={14} /> Accounts Receivable</TabsTrigger>
              <TabsTrigger value="ap" className="gap-1" data-testid="ap-tab"><ArrowUpRight size={14} /> Accounts Payable</TabsTrigger>
            </TabsList>

            <TabsContent value="ar">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Card className="border-success/30 card-enter"><CardContent className="p-4 text-center">
                  <p className="text-[10px] text-success uppercase tracking-wider font-medium">Total AR Outstanding</p>
                  <p className="text-3xl font-bold text-success inline-flex items-center justify-center gap-1 mt-1" data-testid="ar-total"><RiyalIcon size="0.65em" /> {(arData?.total_outstanding || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{arData?.total_customers || 0} customers</p>
                </CardContent></Card>
                <Card><CardContent className="p-2 h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summaryChart(arData?.summary)}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="value" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent></Card>
              </div>
              <Card><CardContent className="p-0">
                <AgingTable rows={arData?.customers || []} nameField="customer" testId="ar-table" />
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="ap">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <Card className="border-error/30 card-enter"><CardContent className="p-4 text-center">
                  <p className="text-[10px] text-error uppercase tracking-wider font-medium">Total AP Outstanding</p>
                  <p className="text-3xl font-bold text-error inline-flex items-center justify-center gap-1 mt-1" data-testid="ap-total"><RiyalIcon size="0.65em" /> {(apData?.total_outstanding || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{apData?.total_suppliers || 0} suppliers</p>
                </CardContent></Card>
                <Card><CardContent className="p-2 h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summaryChart(apData?.summary)}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="value" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent></Card>
              </div>
              <Card><CardContent className="p-0">
                <AgingTable rows={apData?.suppliers || []} nameField="supplier" testId="ap-table" />
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
